import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { gsap } from 'gsap';
import './Dashboardlayout.css';

const MOBILE_BREAKPOINT = 768;

const DashboardLayout = ({ children, activePath }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= MOBILE_BREAKPOINT);

  const sidebarRef = useRef(null);
  const overlayRef = useRef(null);
  const headerRef = useRef(null);
  const sec1Ref = useRef(null);
  const sec2Ref = useRef(null);
  const sec3Ref = useRef(null);
  const logoutRef = useRef(null);
  const closeRef = useRef(null);
  const didMountAnim = useRef(false);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= MOBILE_BREAKPOINT;
      setIsMobile(mobile);
      if (!mobile) {
        setSidebarOpen(false);
        gsap.set(sidebarRef.current, { x: 0, opacity: 1 });
        if (overlayRef.current) gsap.set(overlayRef.current, { opacity: 0, pointerEvents: 'none' });
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (didMountAnim.current) return;
    didMountAnim.current = true;

    const mobile = window.innerWidth <= MOBILE_BREAKPOINT;

    if (mobile) {
      gsap.set(sidebarRef.current, { x: '-100%', opacity: 1 });
      gsap.set(headerRef.current, { y: -20, opacity: 0 });
      gsap.to(headerRef.current, { y: 0, opacity: 1, duration: 0.5, ease: 'power3.out', delay: 4 });
    } else {
      const tl = gsap.timeline({ defaults: { ease: 'power3.out' }, delay: 4 });
      gsap.set(sidebarRef.current, { x: -40, opacity: 0 });
      gsap.set(headerRef.current, { y: -20, opacity: 0 });
      gsap.set([sec1Ref.current, sec2Ref.current, sec3Ref.current], { x: -20, opacity: 0 });
      gsap.set(logoutRef.current, { opacity: 0, y: 10 });
      tl.to(sidebarRef.current, { x: 0, opacity: 1, duration: 0.7 })
        .to(headerRef.current, { y: 0, opacity: 1, duration: 0.5 }, '-=0.4')
        .to(sec1Ref.current, { x: 0, opacity: 1, duration: 0.4 }, '-=0.3')
        .to(sec2Ref.current, { x: 0, opacity: 1, duration: 0.4 }, '-=0.25')
        .to(sec3Ref.current, { x: 0, opacity: 1, duration: 0.4 }, '-=0.25')
        .to(logoutRef.current, { opacity: 1, y: 0, duration: 0.4 }, '-=0.1');
    }
  }, []);

  const openSidebar = () => {
    setSidebarOpen(true);
    gsap.set(overlayRef.current, { display: 'block' });
    gsap.fromTo(
      overlayRef.current,
      { opacity: 0 },
      { opacity: 1, duration: 0.3, ease: 'power2.out' }
    );
    gsap.fromTo(
      sidebarRef.current,
      { x: '-100%' },
      { x: '0%', duration: 0.45, ease: 'power3.out' }
    );
  };

  const closeSidebar = () => {
    gsap.to(sidebarRef.current, {
      x: '-100%',
      duration: 0.38,
      ease: 'power3.in',
      onComplete: () => setSidebarOpen(false),
    });
    gsap.to(overlayRef.current, {
      opacity: 0,
      duration: 0.3,
      ease: 'power2.in',
      onComplete: () => gsap.set(overlayRef.current, { display: 'none' }),
    });
  };

  const handleNavClick = () => {
    if (isMobile) closeSidebar();
  };

  return (
    <div className="dl-root">

      <aside className="dl-sidebar" ref={sidebarRef}>

        <div className="dl-sidebar__logo">
          <div className="dl-logo-box">
            <span className="dl-logo-box__inner" />
          </div>
          <div className="dl-logo-text">
            <span className="bg-title">THE DESIGN SHOW</span>
            <span className="smal-title">ADMIN DASHBOARD</span>
          </div>
          {isMobile && (
            <button className="dl-sidebar__close" ref={closeRef} onClick={closeSidebar}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"/>
                <line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          )}
        </div>

        <nav className="dl-nav">

          <div className="dl-nav__section" ref={sec1Ref}>
            <p className="dl-nav__section-label">OPERATIONS</p>
            <ul className="dl-nav__list">
              <li>
                <Link to="/home" className={`dl-nav__item no-underline-link ${activePath === '/dashboard' ? 'dl-nav__item--active' : ''}`} onClick={handleNavClick}>
                  <span className="dl-nav__item-icon">
                    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="3" width="7" height="7" rx="1"/>
                      <rect x="14" y="3" width="7" height="7" rx="1"/>
                      <rect x="3" y="14" width="7" height="7" rx="1"/>
                      <rect x="14" y="14" width="7" height="7" rx="1"/>
                    </svg>
                  </span>
                  <span className="dl-nav__item-label">Analytics Dashboard</span>
                </Link>
              </li>
              <li>
                <Link to="/users" className={`dl-nav__item no-underline-link ${activePath === '/dashboard/users' ? 'dl-nav__item--active' : ''}`} onClick={handleNavClick}>
                  <span className="dl-nav__item-icon">
                    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                      <circle cx="9" cy="7" r="4"/>
                      <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                      <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                    </svg>
                  </span>
                  <span className="dl-nav__item-label">User Access Manager</span>
                </Link>
              </li>
              <li>
                <Link to="/booking" className={`dl-nav__item no-underline-link ${activePath === '/dashboard/payments' ? 'dl-nav__item--active' : ''}`} onClick={handleNavClick}>
                  <span className="dl-nav__item-icon">
                    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/>
                      <line x1="1" y1="10" x2="23" y2="10"/>
                    </svg>
                  </span>
                  <span className="dl-nav__item-label">Booking & Payments</span>
                </Link>
              </li>
            </ul>
          </div>

          <div className="dl-nav__section" ref={sec2Ref}>
            <p className="dl-nav__section-label">WEBSITE CMS</p>
            <ul className="dl-nav__list">
              <li>
                <Link to="/nav&footer" className={`dl-nav__item no-underline-link ${activePath === '/dashboard/navigation' ? 'dl-nav__item--active' : ''}`} onClick={handleNavClick}>
                  <span className="dl-nav__item-icon">
                    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="3" y1="6" x2="21" y2="6"/>
                      <line x1="3" y1="12" x2="15" y2="12"/>
                      <line x1="3" y1="18" x2="18" y2="18"/>
                    </svg>
                  </span>
                  <span className="dl-nav__item-label">Navigation & Footer</span>
                </Link>
              </li>
              <li>
                <Link to="/site-content" className={`dl-nav__item no-underline-link ${activePath === '/dashboard/site-content' ? 'dl-nav__item--active' : ''}`} onClick={handleNavClick}>
                  <span className="dl-nav__item-icon">
                    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="3" width="18" height="18" rx="2"/>
                      <path d="M3 9h18"/>
                      <path d="M9 21V9"/>
                    </svg>
                  </span>
                  <span className="dl-nav__item-label">Site Content</span>
                </Link>
              </li>
            </ul>
          </div>

          <div className="dl-nav__section" ref={sec3Ref}>
            <p className="dl-nav__section-label">EXHIBITORS & SYSTEM</p>
            <ul className="dl-nav__list">
              <li>
                <Link to="/exhibitors" className={`dl-nav__item no-underline-link ${activePath === '/dashboard/exhibitors' ? 'dl-nav__item--active' : ''}`} onClick={handleNavClick}>
                  <span className="dl-nav__item-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 15 15" fill="none">
                      <path d="M4.87012 1.21777V3.65278" stroke="currentColor" strokeWidth="1.2175" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M9.74011 1.21777V3.65278" stroke="currentColor" strokeWidth="1.2175" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M11.5664 2.43555H3.04392C2.37151 2.43555 1.82642 2.98064 1.82642 3.65305V12.1756C1.82642 12.848 2.37151 13.3931 3.04392 13.3931H11.5664C12.2388 13.3931 12.7839 12.848 12.7839 12.1756V3.65305C12.7839 2.98064 12.2388 2.43555 11.5664 2.43555Z" stroke="currentColor" strokeWidth="1.2175" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M1.82642 6.08789H12.7839" stroke="currentColor" strokeWidth="1.2175" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </span>
                  <span className="dl-nav__item-label">Exhibitor Directory</span>
                </Link>
              </li>
              <li>
                <Link to="/brands" className={`dl-nav__item no-underline-link ${activePath === '/dashboard/brands' ? 'dl-nav__item--active' : ''}`} onClick={handleNavClick}>
                  <span className="dl-nav__item-icon">
                    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 2L2 7l10 5 10-5-10-5z"/>
                      <path d="M2 17l10 5 10-5"/>
                      <path d="M2 12l10 5 10-5"/>
                    </svg>
                  </span>
                  <span className="dl-nav__item-label">Brand Details</span>
                </Link>
              </li>
              <li>
                <Link to="/errors" className={`dl-nav__item no-underline-link ${activePath === '/dashboard/errors' ? 'dl-nav__item--active' : ''}`} onClick={handleNavClick}>
                  <span className="dl-nav__item-icon">
                    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                      <line x1="12" y1="9" x2="12" y2="13"/>
                      <line x1="12" y1="17" x2="12.01" y2="17"/>
                    </svg>
                  </span>
                  <span className="dl-nav__item-label">Error Pages</span>
                </Link>
              </li>
            </ul>
          </div>

        </nav>

        <Link to="/" className="no-underline-link dl-logout" ref={logoutRef}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
            <polyline points="16 17 21 12 16 7"/>
            <line x1="21" y1="12" x2="9" y2="12"/>
          </svg>
          <span>Logout</span>
        </Link>

      </aside>

      <div
        className="dl-overlay"
        ref={overlayRef}
        onClick={closeSidebar}
        style={{ display: 'none', opacity: 0 }}
      />

      <div className="dl-main">

        <header className="dl-header" ref={headerRef}>

          <button className="dl-header__hamburger" onClick={openSidebar}>
            <svg width="30" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="3" y1="6" x2="21" y2="6"/>
              <line x1="3" y1="12" x2="21" y2="12"/>
              <line x1="3" y1="18" x2="21" y2="18"/>
            </svg>
          </button>

          <div className="dl-header__search">
            <span className="dl-header__search-icon">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8"/>
                <line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
            </span>
            <input type="text" className="dl-header__search-input" placeholder="Search..." />
          </div>

          <div className="dl-header__actions">

            <button className="dl-header__bell">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
                <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
              </svg>
              <span className="dl-header__bell-dot" />
            </button>

            <div className="dl-header__user">
              <div className="dl-header__avatar">AD</div>
              <div className="dl-header__user-info">
                <span className="dl-header__user-name">Admin User</span>
                <span className="dl-header__user-email">raia@gmail.com</span>
              </div>
            </div>

          </div>

        </header>

        <main className="dl-content">
          {children}
        </main>

      </div>

    </div>
  );
};

export default DashboardLayout;